import type { ReactNode } from "react";

/**
 * Minimal line-icon set for the Consequence Room (IncidentTimeline).
 * Replaces raw colour emoji (👀 🐸 🎭 …) — those render inconsistently
 * across OS/browsers and read as playful/childish, which is wrong for a
 * B2B tool aimed at senior management. These are plain single-colour
 * stroke icons, same visual language across every event.
 */
type IconKind =
  | "wifi"
  | "eye"
  | "call"
  | "laptop"
  | "plug"
  | "mobile"
  | "faceid"
  | "desktop"
  | "lock"
  | "list";

const PATHS: Record<IconKind, ReactNode> = {
  wifi: (
    <>
      <path d="M2 8.7c5.5-5 14.5-5 20 0" />
      <path d="M5.3 12.2c3.7-3.3 9.7-3.3 13.4 0" />
      <path d="M8.8 15.7c2-1.7 4.4-1.7 6.4 0" />
      <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12c2.6-4.2 6.4-6.4 10-6.4S19.4 7.8 22 12c-2.6 4.2-6.4 6.4-10 6.4S4.6 16.2 2 12Z" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
  call: (
    <path d="M6.6 3.5 9.4 9 6.8 11.4c1.1 2.2 3.2 4.3 5.4 5.4l2.4-2.4 5.5 2.8v3.1c0 1-.9 1.7-1.9 1.5C10.8 20.4 4.5 14.1 3.5 6.4c-.1-1 .6-1.9 1.6-1.9h1.5Z" />
  ),
  laptop: (
    <>
      <rect x="4" y="5" width="16" height="10.2" rx="1.2" />
      <path d="M2 19h20" />
    </>
  ),
  plug: (
    <>
      <path d="M9 3v5M15 3v5M6.5 8h11l-1 6a5 5 0 0 1-9 0Z" />
      <path d="M12 17.5V21" />
    </>
  ),
  mobile: (
    <>
      <rect x="7" y="2.5" width="10" height="19" rx="2" />
      <path d="M10.6 19h2.8" />
    </>
  ),
  faceid: (
    <>
      <path d="M4 8V6a2 2 0 0 1 2-2h2M18 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M6 20H4a2 2 0 0 1-2-2v-2" />
      <circle cx="12" cy="12" r="3.2" />
    </>
  ),
  desktop: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="1.2" />
      <path d="M8 20h8M12 16v4" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="10" rx="1.5" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </>
  ),
  list: (
    <>
      <path d="M8.5 6h12M8.5 12h12M8.5 18h12" />
      <circle cx="4" cy="6" r="1" fill="currentColor" stroke="none" />
      <circle cx="4" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="4" cy="18" r="1" fill="currentColor" stroke="none" />
    </>
  ),
};

export function TimelineIcon({ kind, className }: { kind?: string | null; className?: string }) {
  const paths = PATHS[(kind ?? "") as IconKind] ?? PATHS.list;
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths}
    </svg>
  );
}
